// StudentAccountCreator.tsx
import { SessionContext } from "@/context/SessionContext";
import ConductionAPI, {
    BulkRegisterStudentItem,
    BulkRegisterStudentResult,
} from "@/javascript/conduction-api";
import { Utils } from "@/javascript/utils";
import {
    Alert,
    Button,
    Center,
    FileButton,
    Flex,
    Group,
    Modal,
    ScrollArea,
    Stack,
    Table,
    Text,
    TextInput,
} from "@mantine/core";
import { IconAlertCircle, IconCheck, IconPlus, IconX } from "@tabler/icons-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

type StudentRow = {
    id: string;
    firstName: string;
    lastName: string;
    username: string;
    password: string;
};

export type StudentAccountCreatorModalProps = {
    opened: boolean;
    onClose: () => void;

    /** Required for calling the bulk registration endpoint */
    classroomId: string;

    /**
     * Optional callback after a successful bulk creation
     * (usernames may be altered server-side for uniqueness).
     */
    onCreate?: () => void;

    title?: string;
};

function normalizeLetters(input: string): string {
    return input
        .toLowerCase()
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .replace(/[^a-z]/g, "");
}
function uniqueUsername(base: string, existing: Set<string>): string {
    if (!existing.has(base)) {
        existing.add(base);
        return base;
    }
    let i = 2;
    while (existing.has(`${base}${i}`)) i++;
    const candidate = `${base}${i}`;
    existing.add(candidate);
    return candidate;
}

const COLORS = ["red", "blue", "green", "purple", "orange", "yellow", "pink", "teal", "white", "black", "silver", "gold"];
const ANIMALS = ["cat", "dog", "bird", "fox", "bear", "lion", "tiger", "zebra", "panda", "koala", "eagle", "horse"];
const SIMPLE = ["cloud", "rain", "sun", "moon", "star", "tree", "rock", "wind", "fire", "fun", "music", "note", "tone", "sound"];
const WORDS = [...COLORS, ...ANIMALS, ...SIMPLE];

function rand<T>(arr: T[]): T {
    return arr[Math.floor(Math.random() * arr.length)];
}

/** 6–29 chars; concatenate words until >=6; avoid exceeding 29 if possible */
function generateLengthBoundPassword(): string {
    const MIN = 6;
    const MAX = 29;
    let pwd = "";
    for (let i = 0; i < 20; i++) {
        const next = rand(WORDS);
        if (pwd.length >= MIN) break;
        if (pwd.length + next.length > MAX) {
            let fitted = "";
            for (let j = 0; j < 10; j++) {
                const candidate = rand(WORDS);
                if (pwd.length + candidate.length <= MAX) {
                    fitted = candidate;
                    break;
                }
            }
            if (fitted) pwd += fitted;
            else break;
        } else {
            pwd += next;
        }
    }
    while (pwd.length < MIN) {
        pwd += String.fromCharCode(97 + Math.floor(Math.random() * 26));
    }
    return pwd;
}

function newEmptyRow(): StudentRow {
    return { id: crypto.randomUUID(), firstName: "", lastName: "", username: "", password: "" };
}

const headerTextProps = { size: "sm" as const, fw: 600 as const };

/* ---------- Minimal PDF builder (single page) ---------- */
function toAscii(input: string): string {
    return input.replace(/[^\x20-\x7E]/g, "?");
}
function pdfEscape(s: string): string {
    return toAscii(s).replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");
}
function buildPdfBlob(rows: StudentRow[]): Blob {
    // --- Layout constants (Letter: 612x792 points) ---
    const PAGE_W = 612;
    const PAGE_H = 792;
    const MARGIN = 72; // 1 inch
    const LEFT = MARGIN;

    // Column widths (sum must equal TABLE_W)
    // #: 24, First: 110, Last: 110, Username: 130, Password: 94 => 468 total
    const COLS = [
        { title: "#", width: 24 },
        { title: "First Name", width: 110 },
        { title: "Last Name", width: 110 },
        { title: "Username", width: 130 },
        { title: "Password", width: 94 },
    ] as const;
    const COL_COUNT = COLS.length;
    const SUM_W = COLS.reduce((a, c) => a + c.width, 0);
    const TABLE_LEFT = LEFT;
    const TABLE_RIGHT = LEFT + SUM_W;

    // Typography & spacing
    const TITLE_FS = 14;
    const META_FS = 10;
    const CELL_FS = 10;
    const HEADER_FS = 10;
    const CELL_PAD_X = 6; // left padding inside cells
    const HEADER_H = 24;
    const ROW_H = 20;

    // Top anchors
    const title = "Student Accounts";
    const generated = `Generated: ${new Date().toLocaleString()}`;
    const TITLE_Y = PAGE_H - MARGIN;
    const META_Y = TITLE_Y - 16;
    const TABLE_TOP_Y = META_Y - 24;

    // Compute how many rows fit on a single page (no pagination for simplicity)
    const availableHeight = TABLE_TOP_Y - MARGIN;
    const maxRows = Math.max(0, Math.floor((availableHeight - HEADER_H) / ROW_H));
    const renderRows = rows.slice(0, maxRows);
    const TABLE_H = HEADER_H + renderRows.length * ROW_H;
    const TABLE_BOTTOM_Y = TABLE_TOP_Y - TABLE_H;

    // Helper: truncate to fit column width for Helvetica 10pt
    const avgCharW = CELL_FS * 0.5; // rough average width in Helvetica
    const ellipsis = "…";
    const fit = (text: string, colWidth: number) => {
        const maxPx = colWidth - CELL_PAD_X * 2;
        const maxChars = Math.max(0, Math.floor(maxPx / avgCharW));
        if (maxChars <= 0) return "";
        if (text.length <= maxChars) return text;
        if (maxChars <= 1) return text.slice(0, 1);
        return text.slice(0, maxChars - 1) + ellipsis;
    };

    // Build content stream
    let content = "";

    // Title
    content += `BT\n/F2 ${TITLE_FS} Tf\n1 0 0 1 ${LEFT} ${TITLE_Y} Tm\n(${pdfEscape(title)}) Tj\nET\n`;
    // Meta
    content += `BT\n/F1 ${META_FS} Tf\n1 0 0 1 ${LEFT} ${META_Y} Tm\n(${pdfEscape(generated)}) Tj\nET\n`;

    // Draw table grid
    // Stroke setup
    content += `0 G\n0.7 w\n`; // black stroke, 0.7pt line

    // Outer rectangle
    content += `${TABLE_LEFT} ${TABLE_BOTTOM_Y} ${SUM_W} ${TABLE_H} re S\n`;

    // Horizontal lines (header bottom + each data row)
    const headerBottomY = TABLE_TOP_Y - HEADER_H;
    // Header bottom line (already part of outer rect, but stroke again for clarity)
    content += `${TABLE_LEFT} ${headerBottomY} m ${TABLE_RIGHT} ${headerBottomY} l S\n`;
    for (let r = 0; r <= renderRows.length; r++) {
        const y = headerBottomY - r * ROW_H;
        content += `${TABLE_LEFT} ${y} m ${TABLE_RIGHT} ${y} l S\n`;
    }

    // Vertical lines at each column boundary
    let xCursor = TABLE_LEFT;
    content += `${xCursor} ${TABLE_BOTTOM_Y} m ${xCursor} ${TABLE_TOP_Y} l S\n`;
    for (let i = 0; i < COL_COUNT; i++) {
        xCursor += COLS[i].width;
        content += `${xCursor} ${TABLE_BOTTOM_Y} m ${xCursor} ${TABLE_TOP_Y} l S\n`;
    }

    // Header labels
    let x = TABLE_LEFT;
    for (let c = 0; c < COL_COUNT; c++) {
        const col = COLS[c];
        const headerText = fit(col.title, col.width);
        const tx = x + CELL_PAD_X;
        const ty = TABLE_TOP_Y - 14; // baseline inside header
        content += `BT\n/F2 ${HEADER_FS} Tf\n1 0 0 1 ${tx} ${ty} Tm\n(${pdfEscape(headerText)}) Tj\nET\n`;
        x += col.width;
    }

    // Data rows
    for (let r = 0; r < renderRows.length; r++) {
        const rowTop = headerBottomY - r * ROW_H;
        const baseY = rowTop - 14; // baseline inside row
        const row = renderRows[r];

        const cells = [
            String(r + 1),
            row.firstName || "",
            row.lastName || "",
            row.username || "",
            row.password || "",
        ];

        let cx = TABLE_LEFT;
        for (let c = 0; c < COL_COUNT; c++) {
            const col = COLS[c];
            const txt = fit(cells[c], col.width);
            const tx = cx + CELL_PAD_X;
            content += `BT\n/F1 ${CELL_FS} Tf\n1 0 0 1 ${tx} ${baseY} Tm\n(${pdfEscape(txt)}) Tj\nET\n`;
            cx += col.width;
        }
    }

    // Assemble minimal PDF
    const objects: string[] = [];
    // Catalog
    objects[1] = `<< /Type /Catalog /Pages 2 0 R >>`;
    // Pages
    objects[2] = `<< /Type /Pages /Kids [3 0 R] /Count 1 >>`;
    // Page (resources include two fonts: Helvetica and Helvetica-Bold)
    objects[3] =
        `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_W} ${PAGE_H}] ` +
        `/Resources << /Font << /F1 4 0 R /F2 6 0 R >> >> /Contents 5 0 R >>`;
    // Font: Helvetica
    objects[4] = `<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>`;
    // Contents
    objects[5] = `<< /Length ${content.length} >>\nstream\n${content}\nendstream`;
    // Font: Helvetica-Bold
    objects[6] = `<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>`;

    let pdf = "%PDF-1.4\n";
    const xref: number[] = [];
    const add = (n: number, body: string) => {
        xref[n] = pdf.length;
        pdf += `${n} 0 obj\n${body}\nendobj\n`;
    };
    add(1, objects[1]);
    add(2, objects[2]);
    add(3, objects[3]);
    add(4, objects[4]);
    add(5, objects[5]);
    add(6, objects[6]);

    const xrefStart = pdf.length;
    const COUNT = 7; // objects 0..6 (0 is the free object)
    pdf += `xref\n0 ${COUNT}\n`;
    pdf += "0000000000 65535 f \n";
    for (let i = 1; i < COUNT; i++) {
        const off = String(xref[i]).padStart(10, "0");
        pdf += `${off} 00000 n \n`;
    }
    pdf += `trailer << /Size ${COUNT} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;

    return new Blob([pdf], { type: "application/pdf" });
}

/* ---------- Row error plumbing ---------- */
type FieldKey = "firstName" | "lastName" | "username" | "password";
type RowFieldErrors = Partial<Record<FieldKey, string>>;
type RowErrorsState = Record<string, { fields: RowFieldErrors; general: string[] }>;

function classifyFieldFromMessage(msg: string): FieldKey | undefined {
    const m = msg.toLowerCase();
    if (m.includes("password")) return "password";
    if (m.includes("username")) return "username";
    if (m.includes("first")) return "firstName";
    if (m.includes("last")) return "lastName";
    return undefined;
}

const StudentAccountCreatorModal: React.FC<StudentAccountCreatorModalProps> = ({
    opened,
    onClose,
    onCreate,
    classroomId,
    title = "Create Student Accounts",
}) => {
    const [rows, setRows] = useState<StudentRow[]>([newEmptyRow()]);

    // flash red for missing names when generating usernames locally
    const [flashFirst, setFlashFirst] = useState<Set<string>>(new Set());
    const [flashLast, setFlashLast] = useState<Set<string>>(new Set());
    // flash red for required username/password on submit
    const [flashUser, setFlashUser] = useState<Set<string>>(new Set());
    const [flashPass, setFlashPass] = useState<Set<string>>(new Set());
    // flash yellow after server modifies usernames
    const [flashYellowUsernames, setFlashYellowUsernames] = useState<Set<string>>(new Set());

    const flashTimerRef = useRef<number | null>(null);
    const flashYellowTimerRef = useRef<number | null>(null);

    const importResetRef = useRef<() => void>(null);

    // show exports + warning after a successful server-side generation
    const [showExports, setShowExports] = useState(false);

    // per-row errors from server (and local required validation)
    const [rowErrors, setRowErrors] = useState<RowErrorsState>({});

    // general/server-level errors not tied to a row
    const [globalErrors, setGlobalErrors] = useState<string[]>([]);

    const triggerNameFlash = useCallback((firstIds: string[], lastIds: string[]) => {
        setFlashFirst((prev) => new Set([...prev, ...firstIds]));
        setFlashLast((prev) => new Set([...prev, ...lastIds]));
        if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
        flashTimerRef.current = window.setTimeout(() => {
            setFlashFirst((prev) => {
                const next = new Set(prev);
                firstIds.forEach((id) => next.delete(id));
                return next;
            });
            setFlashLast((prev) => {
                const next = new Set(prev);
                lastIds.forEach((id) => next.delete(id));
                return next;
            });
        }, 1200);
    }, []);

    const triggerCredsFlash = useCallback((userIds: string[], passIds: string[]) => {
        setFlashUser((prev) => new Set([...prev, ...userIds]));
        setFlashPass((prev) => new Set([...prev, ...passIds]));
        if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
        flashTimerRef.current = window.setTimeout(() => {
            setFlashUser((prev) => {
                const next = new Set(prev);
                userIds.forEach((id) => next.delete(id));
                return next;
            });
            setFlashPass((prev) => {
                const next = new Set(prev);
                passIds.forEach((id) => next.delete(id));
                return next;
            });
        }, 1200);
    }, []);

    const triggerYellowFlash = useCallback((ids: string[]) => {
        if (!ids.length) return;
        setFlashYellowUsernames((prev) => new Set([...prev, ...ids]));
        if (flashYellowTimerRef.current) window.clearTimeout(flashYellowTimerRef.current);
        flashYellowTimerRef.current = window.setTimeout(() => {
            setFlashYellowUsernames((prev) => {
                const next = new Set(prev);
                ids.forEach((id) => next.delete(id));
                return next;
            });
        }, 1600);
    }, []);

    useEffect(() => {
        return () => {
            if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
            if (flashYellowTimerRef.current) window.clearTimeout(flashYellowTimerRef.current);
        };
    }, []);

    const setCell = useCallback((id: string, key: keyof Omit<StudentRow, "id">, value: string) => {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)));
        // clear field-specific error as user edits
        setRowErrors((prev) => {
            const existing = prev[id];
            if (!existing) return prev;
            const fields = { ...existing.fields };
            delete fields[key as FieldKey];
            const next = { ...prev, [id]: { fields, general: existing.general } };
            if (Object.keys(next[id].fields).length === 0 && next[id].general.length === 0) {
                const clone = { ...next };
                delete clone[id];
                return clone;
            }
            return next;
        });
    }, []);

    const addRow = useCallback(() => setRows((prev) => [...prev, newEmptyRow()]), []);
    const deleteRow = useCallback((id: string) => {
        setRows((prev) => {
            const next = prev.filter((r) => r.id !== id);
            return next.length ? next : [newEmptyRow()];
        });
        setRowErrors((prev) => {
            const clone = { ...prev };
            delete clone[id];
            return clone;
        });
    }, []);

    const generateUsernamesForMissing = useCallback(() => {
        const flashFirstIds: string[] = [];
        const flashLastIds: string[] = [];
        setRows((prev) => {
            const existing = new Set(
                prev
                    .map((r) => r.username.trim())
                    .filter((u) => u.length > 0)
                    .map((u) => normalizeLetters(u))
            );
            const updated = prev.map((r) => {
                if (r.username.trim().length > 0) return r;
                const fnRaw = (r.firstName || "").trim();
                const lnRaw = (r.lastName || "").trim();
                const firstMissing = fnRaw.length === 0;
                const lastMissing = lnRaw.length === 0;
                if (firstMissing || lastMissing) {
                    if (firstMissing) flashFirstIds.push(r.id);
                    if (lastMissing) flashLastIds.push(r.id);
                    return r;
                }
                const fn = normalizeLetters(fnRaw);
                const ln = normalizeLetters(lnRaw);
                const base = `${fn}${ln[0] ?? ""}`;
                const unique = uniqueUsername(base, existing);
                return { ...r, username: unique };
            });
            return updated;
        });
        if (flashFirstIds.length || flashLastIds.length) triggerNameFlash(flashFirstIds, flashLastIds);

        // Remove username errors
        setRowErrors((prev) => {
            const cleaned: RowErrorsState = {};
            for (const [id, val] of Object.entries(prev)) {
                const cur = { ...val };
                delete cur.fields.username;
                if (Object.keys(cur.fields).length === 0 && cur.general.length === 0) {
                    continue;
                }
                cleaned[id] = cur;
            }
            return cleaned;
        });
    }, [triggerNameFlash]);

    const generatePasswordsForMissing = useCallback(() => {
        setRows((prev) =>
            prev.map((r) => (r.password.trim().length > 0 ? r : { ...r, password: generateLengthBoundPassword() }))
        );

        // Remove errors for passwords as we generate them
        setRowErrors((prev) => {
            const cleaned: RowErrorsState = {};
            for (const [id, val] of Object.entries(prev)) {
                const cur = { ...val };
                delete cur.fields.password;
                if (Object.keys(cur.fields).length === 0 && cur.general.length === 0) {
                    continue;
                }
                cleaned[id] = cur;
            }
            return cleaned;
        });
    }, []);

    const session = React.useContext(SessionContext);
    if (session === undefined) {
        throw new Error("This component must be a child of SessionContext.");
    }

    /** Validate required username/password fields before calling the API. Returns true if valid. */
    const validateRequiredCreds = useCallback((): boolean => {
        const missingUserIds: string[] = [];
        const missingPassIds: string[] = [];
        const nextRowErrors: RowErrorsState = { ...rowErrors };

        rows.forEach((r) => {
            const uEmpty = !r.username || r.username.trim().length === 0;
            const pEmpty = !r.password || r.password.trim().length === 0;

            if (uEmpty) missingUserIds.push(r.id);
            if (pEmpty) missingPassIds.push(r.id);

            if (uEmpty || pEmpty) {
                const cur = nextRowErrors[r.id] ?? { fields: {}, general: [] };
                if (uEmpty) cur.fields.username = "Username is required.";
                if (pEmpty) cur.fields.password = "Password is required.";
                nextRowErrors[r.id] = cur;
            } else {
                if (nextRowErrors[r.id]) {
                    const cur = { ...nextRowErrors[r.id] };
                    delete cur.fields.username;
                    delete cur.fields.password;
                    if (Object.keys(cur.fields).length === 0 && cur.general.length === 0) {
                        delete nextRowErrors[r.id];
                    } else {
                        nextRowErrors[r.id] = cur;
                    }
                }
            }
        });

        if (missingUserIds.length || missingPassIds.length) {
            setRowErrors(nextRowErrors);
            triggerCredsFlash(missingUserIds, missingPassIds);
            Utils.displayNotification(
                "Please fill in all required Username and Password fields.",
                undefined,
                "red",
                <IconAlertCircle size={16} />,
            );
            return false;
        }

        return true;
    }, [rows, rowErrors, triggerCredsFlash]);

    /* ---------- Bulk create (server) ---------- */
    const handleGenerateAccounts = useCallback(async () => {
        // Block if required fields are missing
        if (!validateRequiredCreds()) return;
        if (session?.token === null) return;

        const notifId = Utils.displayNotification(
            "Creating student accounts...",
            undefined,
            "blue",
            "loading"
        );

        setGlobalErrors([]);
        setRowErrors((prev) => {
            // keep any unrelated (non-username/password) errors; clear general
            const cleaned: RowErrorsState = {};
            for (const [id, val] of Object.entries(prev)) {
                cleaned[id] = { fields: { ...val.fields }, general: [] };
            }
            return cleaned;
        });
        setShowExports(false);

        try {
            const items: BulkRegisterStudentItem[] = rows.map((r) => ({
                username: r.username?.trim() || undefined,
                password: r.password,
                firstName: r.firstName?.trim() || undefined,
                lastName: r.lastName?.trim() || undefined,
            }));

            const result = await ConductionAPI.registerStudentsBulk(classroomId, items, session.token);

            if (result.success) {
                // Map results by index (server uses 0-based index in our DTO)
                const byIndex = new Map<number, BulkRegisterStudentResult>();
                result.results.forEach((r) => byIndex.set(r.index, r));

                const changedIds: string[] = [];

                setRows((prev) =>
                    prev.map((row, i) => {
                        const created = byIndex.get(i);
                        if (!created) return row;
                        // detect server-changed username (e.g., due to uniqueness bump)
                        if (row.username !== created.username) {
                            changedIds.push(row.id);
                        }
                        return { ...row, username: created.username };
                    })
                );

                if (changedIds.length) triggerYellowFlash(changedIds);

                setShowExports(true);

                Utils.updateNotification(
                    notifId,
                    "Student accounts created!",
                    undefined,
                    "green",
                    <IconCheck size={16} />
                );

                if (onCreate) onCreate();
            } else {
                const newRowErrors: RowErrorsState = {};
                const general: string[] = [];

                const pushRowError = (rowIndex1: number, msg: string) => {
                    const zero = rowIndex1 - 1; // server Row N (1-based)
                    const targetRow = rows[zero];
                    if (!targetRow) {
                        general.push(msg);
                        return;
                    }
                    const id = targetRow.id;
                    const field = classifyFieldFromMessage(msg);
                    if (!newRowErrors[id]) newRowErrors[id] = { fields: {}, general: [] };
                    if (field) newRowErrors[id].fields[field] = msg;
                    else newRowErrors[id].general.push(msg);
                };

                (result.errors ?? []).forEach((line) => {
                    const m = /^Row\s+(\d+)\s*:\s*(.+)$/i.exec(line);
                    if (m) {
                        const idx = parseInt(m[1], 10);
                        const msg = m[2].trim();
                        pushRowError(idx, msg);
                    } else {
                        general.push(line);
                    }
                });

                setRowErrors(newRowErrors);
                setGlobalErrors(general);

                Utils.updateNotification(
                    notifId,
                    "We are sorry. There were errors creating some students.",
                    undefined,
                    "red",
                    <IconAlertCircle size={16} />,
                    true,
                    false
                );
            }
        } catch (err) {
            console.error("Bulk creation failed:", err);
            Utils.updateNotification(
                notifId,
                "We are sorry. An unexpected error occurred while creating student accounts.",
                undefined,
                "red",
                <IconAlertCircle size={16} />,
                true,
                false
            );
        }
    }, [rows, classroomId, session?.token, onCreate, validateRequiredCreds, triggerYellowFlash]);

    /* ---------- CSV Export/Import ---------- */
    const toCSV = useCallback((data: StudentRow[]) => {
        const headers = ["First Name", "Last Name", "Username", "Password"];
        const lines = [headers.join(",")];
        const escapeCsv = (val: string) => {
            const needsQuotes = /[",\n]/.test(val);
            const escaped = Utils.replaceAll(val, '"', '""');
            return needsQuotes ? `"${escaped}"` : escaped;
        };
        data.forEach((r) => {
            lines.push([escapeCsv(r.firstName ?? ""), escapeCsv(r.lastName ?? ""), escapeCsv(r.username ?? ""), escapeCsv(r.password ?? "")].join(","));
        });
        return lines.join("\n");
    }, []);

    const downloadCSV = useCallback(() => {
        const csv = toCSV(rows);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const stamp = new Date().toISOString().replace(/[-:]/g, "").replace(/\..+/, "");
        a.href = url;
        a.download = `student-accounts-${stamp}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }, [rows, toCSV]);

    function parseCSV(text: string): string[][] {
        const parsedRows: string[][] = [];
        let cur = "";
        let inQuotes = false;
        let row: string[] = [];
        for (let i = 0; i < text.length; i++) {
            const c = text[i];
            if (inQuotes) {
                if (c === '"') {
                    if (text[i + 1] === '"') {
                        cur += '"';
                        i++;
                    } else {
                        inQuotes = false;
                    }
                } else {
                    cur += c;
                }
            } else {
                if (c === '"') inQuotes = true;
                else if (c === ",") {
                    row.push(cur);
                    cur = "";
                } else if (c === "\n") {
                    row.push(cur);
                    parsedRows.push(row);
                    row = [];
                    cur = "";
                } else if (c !== "\r") {
                    cur += c;
                }
            }
        }
        row.push(cur);
        parsedRows.push(row);
        while (parsedRows.length && parsedRows[parsedRows.length - 1].length === 1 && parsedRows[parsedRows.length - 1][0] === "") {
            parsedRows.pop();
        }
        return parsedRows;
    }

    const importCSV = useCallback(async (file: File | null) => {
        if (!file) return;

        const text = await file.text();
        const grid = parseCSV(text);
        if (grid.length === 0) {
            // if the CSV is empty, fall back to a single empty row
            setRows([newEmptyRow()]);
            importResetRef.current?.();
            return;
        }

        const headers = grid[0].map((h) => h.trim().toLowerCase());
        const colIndex = (names: string[]) => {
            const aliases = names.map((n) => n.toLowerCase());
            return headers.findIndex((h) => aliases.includes(h));
        };

        const idxFirst = colIndex(["first", "first name", "firstname", "first_name", "given", "given name"]);
        const idxLast = colIndex(["last", "last name", "lastname", "last_name", "surname", "family name"]);
        const idxUser = colIndex(["username", "user name", "login"]);
        const idxPass = colIndex(["password", "pass", "pwd"]);

        const imported: StudentRow[] = [];
        for (let r = 1; r < grid.length; r++) {
            const row = grid[r];
            const firstName = idxFirst >= 0 ? (row[idxFirst] ?? "").trim() : "";
            const lastName = idxLast >= 0 ? (row[idxLast] ?? "").trim() : "";
            const username = idxUser >= 0 ? (row[idxUser] ?? "").trim() : "";
            const password = idxPass >= 0 ? (row[idxPass] ?? "").trim() : "";

            // skip completely blank lines
            if (!firstName && !lastName && !username && !password) continue;

            imported.push({ id: crypto.randomUUID(), firstName, lastName, username, password });
        }

        // ✅ Replace all rows with the new import (or reset to a single empty row)
        setRows(imported.length > 0 ? imported : [newEmptyRow()]);

        // important: allow importing again (even the same file)
        importResetRef.current?.();
    }, []);


    const downloadPDF = useCallback(() => {
        const blob = buildPdfBlob(rows);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const stamp = new Date().toISOString().replace(/[-:]/g, "").replace(/\..+/, "");
        a.href = url;
        a.download = `student-accounts-${stamp}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
    }, [rows]);

    // ---- UI state helpers ----
    const canEdit = !showExports; // once generated successfully, lock down the UI

    const usernameHeader = useMemo(
        () => (
            <Group justify="space-between" wrap="nowrap">
                <Text {...headerTextProps}>Username</Text>
                {canEdit && (
                    <Button size="xs" variant="light" onClick={generateUsernamesForMissing}>
                        Generate
                    </Button>
                )}
            </Group>
        ),
        [generateUsernamesForMissing, canEdit]
    );

    const passwordHeader = useMemo(
        () => (
            <Group justify="space-between" wrap="nowrap">
                <Text {...headerTextProps}>Password</Text>
                {canEdit && (
                    <Button size="xs" variant="light" onClick={generatePasswordsForMissing}>
                        Generate
                    </Button>
                )}
            </Group>
        ),
        [generatePasswordsForMissing, canEdit]
    );

    return (
        <Modal
            opened={opened}
            onClose={() => {
                onClose();
                setRows([newEmptyRow()]);
                setGlobalErrors([]);
                setRowErrors({});
                setShowExports(false);
                setFlashYellowUsernames(new Set());
            }}
            title={title}
            size="70vw"
            radius="lg"
            centered
            withCloseButton
            overlayProps={{ blur: 2, opacity: 0.2 }}
        >
            {/* Sticky headers + flash red for missing names/creds; yellow flash for changed usernames; red asterisk style */}
            <style>{`
        .sac-table thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: var(--mantine-color-body);
        }
        @keyframes flashRed {
          0%   { box-shadow: 0 0 0 0 rgba(255,0,0,0.0); border-color: var(--mantine-color-red-6); }
          50%  { box-shadow: 0 0 0 6px rgba(255,0,0,0.15); }
          100% { box-shadow: 0 0 0 0 rgba(255,0,0,0.0); }
        }
        .flash-red {
          animation: flashRed 0.6s ease-out 0s 3;
          border-color: var(--mantine-color-red-6) !important;
        }
        @keyframes flashYellowBg {
          0%   { background-color: transparent; }
          20%  { background-color: var(--mantine-color-yellow-1); }
          60%  { background-color: var(--mantine-color-yellow-2); }
          100% { background-color: transparent; }
        }
        .flash-yellow {
          animation: flashYellowBg 1.4s ease-out 0s 3;
        }
        .asterisk { font-weight: 700; line-height: 1; }
      `}</style>

            <Flex direction="column">
                {/* Global (non-row) errors */}
                {globalErrors.length > 0 && (
                    <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />} mb="sm">
                        <Stack gap={4}>
                            {globalErrors.map((e, i) => (
                                <Text key={i} size="sm">{e}</Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {canEdit ? (
                    <Text>
                        Fill in the details below to create student accounts. Username and Password are required.
                        You can enter them manually, or use the "Generate" buttons to fill in any missing passwords or usernames based on the first and last names.
                        When you're ready, click "Generate Accounts" to proceed.
                    </Text>
                ) : (
                    <Text>
                        These student accounts have been created successfully. Some usernames may have been updated to avoid conflicts with other accounts. You can change these later.
                    </Text>
                )}

                <ScrollArea h="60vh">
                    <Table
                        className="sac-table"
                        striped
                        highlightOnHover
                        withRowBorders
                        withColumnBorders
                        verticalSpacing="xs"
                        horizontalSpacing="md"
                    >
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th style={{ width: 56 }}>
                                    <Text {...headerTextProps} ta="center">#</Text>
                                </Table.Th>
                                <Table.Th><Text {...headerTextProps}>First Name</Text></Table.Th>
                                <Table.Th><Text {...headerTextProps}>Last Name</Text></Table.Th>
                                <Table.Th>{usernameHeader}</Table.Th>
                                <Table.Th>{passwordHeader}</Table.Th>
                                <Table.Th style={{ width: 48 }}><Text {...headerTextProps} ta="center" /></Table.Th>
                            </Table.Tr>
                        </Table.Thead>

                        <Table.Tbody>
                            {rows.map((r, idx) => {
                                const errs = rowErrors[r.id];
                                return (
                                    <React.Fragment key={r.id}>
                                        <Table.Tr>
                                            <Table.Td><Text ta="center">{idx + 1}</Text></Table.Td>

                                            <Table.Td>
                                                {canEdit ? (
                                                    <TextInput
                                                        placeholder="First name"
                                                        value={r.firstName}
                                                        onChange={(e) => setCell(r.id, "firstName", e.currentTarget.value)}
                                                        autoComplete="off"
                                                        readOnly={!canEdit}
                                                        classNames={{ input: flashFirst.has(r.id) ? "flash-red" : undefined }}
                                                        error={errs?.fields.firstName}
                                                    />
                                                ) : (
                                                    <Text className={flashFirst.has(r.id) ? "flash-red" : ""}>{r.firstName}</Text>
                                                )}
                                            </Table.Td>

                                            <Table.Td>
                                                {canEdit ? (
                                                    <TextInput
                                                        placeholder="Last name"
                                                        value={r.lastName}
                                                        onChange={(e) => setCell(r.id, "lastName", e.currentTarget.value)}
                                                        autoComplete="off"
                                                        readOnly={!canEdit}
                                                        classNames={{ input: flashLast.has(r.id) ? "flash-red" : undefined }}
                                                        error={errs?.fields.lastName}
                                                    />
                                                ) : (
                                                    <Text className={flashLast.has(r.id) ? "flash-red" : ""}>{r.lastName}</Text>
                                                )}
                                            </Table.Td>

                                            <Table.Td>
                                                {canEdit ? (
                                                    <TextInput
                                                        placeholder="Username"
                                                        value={r.username}
                                                        onChange={(e) => setCell(r.id, "username", e.currentTarget.value)}
                                                        autoComplete="off"
                                                        readOnly={!canEdit}
                                                        rightSection={<Text c="red.6" className="asterisk">*</Text>}
                                                        rightSectionPointerEvents="none"
                                                        classNames={{
                                                            input: [
                                                                flashUser.has(r.id) ? "flash-red" : "",
                                                                flashYellowUsernames.has(r.id) ? "flash-yellow" : ""
                                                            ].filter(Boolean).join(" ") || undefined
                                                        }}
                                                        error={errs?.fields.username}
                                                    />
                                                ) : (
                                                    <Text className={flashYellowUsernames.has(r.id) ? "flash-yellow" : ""}>{r.username}</Text>
                                                )}
                                            </Table.Td>

                                            <Table.Td>
                                                {canEdit ? (
                                                    <TextInput
                                                        placeholder="Password"
                                                        value={r.password}
                                                        onChange={(e) => setCell(r.id, "password", e.currentTarget.value)}
                                                        autoComplete="off"
                                                        readOnly={!canEdit}
                                                        rightSection={<Text c="red.6" className="asterisk">*</Text>}
                                                        rightSectionPointerEvents="none"
                                                        classNames={{ input: flashPass.has(r.id) ? "flash-red" : undefined }}
                                                        error={errs?.fields.password}
                                                    />
                                                ) : (
                                                    <Text>{r.password}</Text>
                                                )}
                                            </Table.Td>

                                            {canEdit && (
                                                <Table.Td>
                                                    <Center>
                                                        <Button
                                                            size="xs"
                                                            variant="subtle"
                                                            color="red"
                                                            onClick={() => deleteRow(r.id)}
                                                            title="Delete row"
                                                            aria-label={`Delete row ${idx + 1}`}
                                                            leftSection={<IconX size={14} />}
                                                            disabled={!canEdit}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </Center>
                                                </Table.Td>
                                            )}
                                        </Table.Tr>

                                        {/* Row-level general error banner */}
                                        {errs && errs.general.length > 0 && (
                                            <Table.Tr>
                                                <Table.Td colSpan={6} style={{ paddingTop: 0, paddingBottom: 12 }}>
                                                    <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
                                                        <Stack gap={2}>
                                                            {errs.general.map((g, i) => (
                                                                <Text key={i} size="sm">
                                                                    {g}
                                                                </Text>
                                                            ))}
                                                        </Stack>
                                                    </Alert>
                                                </Table.Td>
                                            </Table.Tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </Table.Tbody>
                    </Table>
                    {canEdit && rows.length < 100 && (
                        <Center mt="md" mb="lg">
                            <Button variant="default" onClick={addRow} leftSection={<IconPlus />} disabled={!canEdit}>
                                Add another row
                            </Button>
                        </Center>
                    )}
                </ScrollArea>

                {/* Footer pinned to bottom */}
                <Stack
                    gap="xs"
                    style={{
                        borderTop: "1px solid var(--mantine-color-default-border)",
                        paddingTop: 12,
                        flexShrink: 0,
                    }}
                >
                    {showExports && (
                        <>
                            <Text c="red.6" size="md" mt="md">
                                Note: Passwords will become hidden after you exit this menu (they can still be
                                edited later if necessary). It is highly recommended to record these student
                                credentials now.
                            </Text>
                            <Group justify="space-between" wrap="wrap">
                                <Group>
                                    <Button variant="light" onClick={downloadPDF}>Download PDF</Button>
                                    <Button variant="light" onClick={downloadCSV}>Download CSV</Button>
                                    {/* Import CSV intentionally hidden after generation */}
                                </Group>
                                {/* No action buttons after generation; focus is on recording/exporting */}
                                <Group />
                            </Group>
                        </>
                    )}

                    {canEdit && (
                        <Group justify="space-between" wrap="wrap">
                            <Group>
                                <FileButton
                                    onChange={(file) => importCSV(file)}
                                    accept=".csv,text/csv"
                                    resetRef={importResetRef}
                                >
                                    {(props) => (
                                        <Button variant="outline" {...props}>
                                            Import CSV
                                        </Button>
                                    )}
                                </FileButton>
                            </Group>
                            <Group>
                                <Button onClick={handleGenerateAccounts}>Generate Accounts</Button>
                            </Group>
                        </Group>
                    )}
                </Stack>
            </Flex>
        </Modal>
    );
};

export default StudentAccountCreatorModal;
